import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    Avatar,
    Alert,
    Snackbar,
    CircularProgress
} from '@mui/material';
import { PhotoCamera, Save, Business } from '@mui/icons-material';
import { COLORS } from '../../settings/themeSettings';
import axiosInstance from '../../config/axiosConfig';
import { AGENCE_URL } from '../../settings/APISetting';

const ParametresSociete = () => {
    const [societe, setSociete] = useState({
        nom: '',
        adresse: '',
        telephone: '',
        email: '',
        siteWeb: '',
        rc: '',
        nif: '',
        art: '',
        rib: '',
        logo: null
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ open: false, text: '', severity: 'success' });
    const [agenceId, setAgenceId] = useState(null);
    const [logoChanged, setLogoChanged] = useState(false); // Track if logo was modified

    // Charger les données depuis l'API
    useEffect(() => {
        const fetchAgence = async () => {
            try {
                // paginationEnabled: false donc retourne array directement, pas hydra:member
                const response = await axiosInstance.get(AGENCE_URL);
                const agences = response.data;

                // Si on a des résultats (tableau direct car pagination désactivée)
                if (Array.isArray(agences) && agences.length > 0) {
                    // On prend la première agence trouvée
                    const agenceData = agences[0];
                    setAgenceId(agenceData.id);
                    setSociete({
                        nom: agenceData.nom || '',
                        adresse: agenceData.adresse || '',
                        telephone: agenceData.telephone || '',
                        email: agenceData.email || '',
                        siteWeb: agenceData.siteWeb || '',
                        rc: agenceData.rc || '',
                        nif: agenceData.nif || '',
                        art: agenceData.art || '',
                        rib: agenceData.rib || '',
                        logo: agenceData.logo || null
                    });
                    console.log("Agence chargée:", agenceData);
                } else {
                    // Pas d'agence trouvée, on laisse le formulaire vide mais prêt à créer
                    console.log("Aucune agence trouvée, prêt pour création.");
                }
            } catch (error) {
                console.error("Erreur chargement agence:", error);
                setMessage({ open: true, text: 'Impossible de charger les informations. Vérifiez votre connexion.', severity: 'warning' });
            } finally {
                setLoading(false);
            }
        };

        fetchAgence();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSociete(prev => ({ ...prev, [name]: value }));
    };

    const compressImage = (file, maxWidth = 800, quality = 0.7) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // Redimensionner si l'image est trop grande
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Compression en JPEG avec qualité réduite
                    const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                    resolve(compressedBase64);
                };
                img.onerror = reject;
            };
            reader.onerror = reject;
        });
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                console.log('Taille originale:', file.size, 'bytes');
                const compressedLogo = await compressImage(file, 800, 0.7);
                console.log('Taille compressée:', compressedLogo.length, 'caractères base64');
                setSociete(prev => ({ ...prev, logo: compressedLogo }));
                setLogoChanged(true); // Marquer que le logo a changé
            } catch (error) {
                console.error('Erreur compression image:', error);
                setMessage({ open: true, text: 'Erreur lors du traitement de l\'image', severity: 'error' });
            }
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/ld+json'
                }
            };

            // Préparer le payload : exclure le logo si inchangé lors d'un UPDATE
            let payload = { ...societe };
            if (agenceId && !logoChanged) {
                // UPDATE existant et logo non modifié : on ne l'envoie pas
                delete payload.logo;
                console.log('Logo exclu du payload (inchangé)');
            }

            // Debug: afficher la taille du payload
            const payloadSize = JSON.stringify(payload).length;
            console.log('Taille du payload:', payloadSize, 'caractères');
            if (payload.logo) {
                console.log('Taille du logo base64:', payload.logo.length, 'caractères');
            }

            if (agenceId) {
                // Mise à jour (PUT)
                await axiosInstance.put(`${AGENCE_URL}/${agenceId}`, payload, config);
                setMessage({ open: true, text: 'Paramètres mis à jour avec succès !', severity: 'success' });
                setLogoChanged(false); // Reset du flag
            } else {
                // Création (POST) si aucune agence n'existait
                const response = await axiosInstance.post(AGENCE_URL, payload, config);
                setAgenceId(response.data.id);
                setMessage({ open: true, text: 'Société créée avec succès !', severity: 'success' });
                setLogoChanged(false);
            }

        } catch (error) {
            console.error("Erreur sauvegarde:", error);
            console.error("Détails erreur:", error.response?.data);
            setMessage({ open: true, text: `Erreur: ${error.response?.data?.detail || error.message}`, severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: COLORS.primary, width: 50, height: 50 }}>
                        <Business />
                    </Avatar>
                    <Typography variant="h4" fontWeight="bold" color="text.primary">
                        Paramètres Société
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={24} color="inherit" /> : <Save />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                        bgcolor: COLORS.primary,
                        '&:hover': { bgcolor: COLORS.secondary }
                    }}
                >
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Section Logo et Identité */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={2} sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Logo de l'entreprise</Typography>
                        <Box
                            sx={{
                                width: 150,
                                height: 150,
                                border: '2px dashed #ccc',
                                borderRadius: '50%',
                                margin: '0 auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                position: 'relative',
                                mb: 2
                            }}
                        >
                            {societe.logo ? (
                                <img src={societe.logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <Business sx={{ fontSize: 60, color: '#ccc' }} />
                            )}
                        </Box>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<PhotoCamera />}
                            sx={{ color: COLORS.secondary, borderColor: COLORS.secondary }}
                        >
                            Changer le logo
                            <input hidden accept="image/*" type="file" onChange={handleLogoChange} />
                        </Button>
                        <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                            Recommandé: PNG transparent, 300x300px
                        </Typography>
                    </Paper>
                </Grid>

                {/* Section Informations Générales */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ color: COLORS.secondary, borderBottom: `2px solid ${COLORS.primary}`, display: 'inline-block', mb: 3 }}>
                            Informations Générales
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth label="Raison Sociale / Nom de l'entreprise"
                                    name="nom" value={societe.nom} onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth label="Adresse Complète"
                                    name="adresse" value={societe.adresse} onChange={handleChange}
                                    multiline rows={2}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth label="Téléphone"
                                    name="telephone" value={societe.telephone} onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth label="Email"
                                    name="email" value={societe.email} onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth label="Site Web"
                                    name="siteWeb" value={societe.siteWeb} onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Section Informations Fiscales */}
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ color: COLORS.secondary, borderBottom: `2px solid ${COLORS.primary}`, display: 'inline-block', mb: 3 }}>
                            Informations Légales & Fiscales
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth label="Registre de Commerce (RC)"
                                    name="rc" value={societe.rc} onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth label="Numéro Identification Fiscale (NIF)"
                                    name="nif" value={societe.nif} onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth label="Article d'Imposition (ART)"
                                    name="art" value={societe.art} onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth label="Relevé d'Identité Bancaire (RIB)"
                                    name="rib" value={societe.rib} onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {/* Notification */}
            <Snackbar
                open={message.open}
                autoHideDuration={4000}
                onClose={() => setMessage({ ...message, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={message.severity} sx={{ width: '100%' }}>
                    {message.text}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ParametresSociete;
